<script setup>
import { computed, reactive } from 'vue';
import Message from './Message.vue';
import { MESSAGE_TYPES } from './constants.js';
import { useCamelCase } from 'dashboard/composables/useTransformKeys';
import { useMapGetter } from 'dashboard/composables/store.js';
import MessageApi from 'dashboard/api/inbox/message.js';

/**
 * Props definition for the component
 * @typedef {Object} Props
 * @property {Array} readMessages - Array of read messages
 * @property {Array} unReadMessages - Array of unread messages
 * @property {Number} currentUserId - ID of the current user
 * @property {Boolean} isAnEmailChannel - Whether this is an email channel
 * @property {Object} inboxSupportsReplyTo - Inbox reply support configuration
 * @property {Array} messages - Array of all messages [These are not in camelcase]
 */
const props = defineProps({
  currentUserId: {
    type: Number,
    required: true,
  },
  firstUnreadId: {
    type: Number,
    default: null,
  },
  isAnEmailChannel: {
    type: Boolean,
    default: false,
  },
  inboxSupportsReplyTo: {
    type: Object,
    default: () => ({ incoming: false, outgoing: false }),
  },
  messages: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['retry']);

const allMessages = computed(() => {
  return useCamelCase(props.messages, {
    deep: true,
    stopPaths: [
      'content_attributes.translations',
      'content_attributes.whatsapp_flow_response.response_json',
    ],
  });
});

const ALBUM_QUOTE_PREVIEW = { contentAttributes: { whatsappAlbum: true } };
const ALBUM_MEDIA_TYPES = new Set(['image', 'video']);
const ALBUM_WINDOW_SECONDS = 5 * 60;

const isAlbumStub = message =>
  Boolean(message?.contentAttributes?.whatsappAlbum);

const messageAttrs = message =>
  message?.contentAttributes || message?.content_attributes || {};

const sameMessageId = (left, right) =>
  Number(left) === Number(right) && Number(left) > 0;

const messageHasAlbumMedia = message => {
  if (isAlbumStub(message)) return false;

  return (message.attachments || []).some(attachment =>
    ALBUM_MEDIA_TYPES.has(attachment.fileType || attachment.file_type)
  );
};

const recentAlbumMediaCount = reply => {
  const replyAt = Number(reply.createdAt || reply.created_at || 0);

  return allMessages.value.filter(message => {
    if (!messageHasAlbumMedia(message)) return false;
    if (!replyAt) return true;

    const createdAt = Number(message.createdAt || 0);
    return (
      createdAt > 0 &&
      createdAt <= replyAt &&
      replyAt - createdAt <= ALBUM_WINDOW_SECONDS
    );
  }).length;
};

const isAlbumQuote = message => {
  const attrs = messageAttrs(message);
  if (attrs.inReplyToAlbum || attrs.in_reply_to_album) return true;

  const externalId = String(
    attrs.inReplyToExternalId || attrs.in_reply_to_external_id || ''
  );
  if (!externalId.startsWith('wamid.')) return false;

  return recentAlbumMediaCount(message) >= 2;
};

const visibleMessages = computed(() =>
  allMessages.value.filter(message => !isAlbumStub(message))
);

const firstVisibleUnreadId = computed(() => {
  if (!props.firstUnreadId) return null;

  const start = allMessages.value.findIndex(
    message => message.id === props.firstUnreadId
  );
  if (start === -1) return props.firstUnreadId;

  return (
    allMessages.value.slice(start).find(message => !isAlbumStub(message))?.id ??
    null
  );
});

const currentChat = useMapGetter('getSelectedChat');

// Cache for fetched reply messages to avoid duplicate API calls
const fetchedReplyMessages = reactive(new Map());

/**
 * Fetches a specific message from the API by trying to get messages around it
 * @param {number} messageId - The ID of the message to fetch
 * @param {number} conversationId - The ID of the conversation
 * @returns {Promise<Object|null>} - The fetched message or null if not found/error
 */
const fetchReplyMessage = async (messageId, conversationId) => {
  // Return cached result if already fetched
  if (fetchedReplyMessages.has(messageId)) {
    return fetchedReplyMessages.get(messageId);
  }

  try {
    const response = await MessageApi.getPreviousMessages({
      conversationId,
      before: messageId + 100,
      after: messageId - 100,
    });

    const messages = response.data?.payload || [];
    const targetMessage = messages.find(msg => msg.id === messageId);

    if (targetMessage) {
      const camelCaseMessage = useCamelCase(targetMessage);
      fetchedReplyMessages.set(messageId, camelCaseMessage);
      return camelCaseMessage;
    }

    // Cache null result to avoid repeated API calls
    fetchedReplyMessages.set(messageId, null);
    return null;
  } catch (error) {
    fetchedReplyMessages.set(messageId, null);
    return null;
  }
};

/**
 * Determines if a message should be grouped with the next message
 * @param {Number} index - Index of the current message
 * @param {Array} searchList - Array of messages to check
 * @returns {Boolean} - Whether the message should be grouped with next
 */
const shouldGroupWithNext = (index, searchList) => {
  if (index === searchList.length - 1) return false;

  const current = searchList[index];
  const next = searchList[index + 1];

  if (next.status === 'failed') return false;

  const nextSenderId = next.senderId ?? next.sender?.id;
  const currentSenderId = current.senderId ?? current.sender?.id;
  const hasSameSender = nextSenderId === currentSenderId;

  const nextMessageType = next.messageType;
  const currentMessageType = current.messageType;

  const areBothTemplates =
    nextMessageType === MESSAGE_TYPES.TEMPLATE &&
    currentMessageType === MESSAGE_TYPES.TEMPLATE;

  if (!hasSameSender || areBothTemplates) return false;

  if (currentMessageType !== nextMessageType) return false;

  // Check if messages are in the same minute by rounding down to nearest minute
  return Math.floor(next.createdAt / 60) === Math.floor(current.createdAt / 60);
};

/**
 * Gets the message that was replied to
 * @param {Object} parentMessage - The message containing the reply reference
 * @returns {Object|null} - The message being replied to, or null if not found
 */
const getInReplyToMessage = parentMessage => {
  if (!parentMessage) return null;

  const attrs = messageAttrs(parentMessage);
  const inReplyToMessageId = attrs.inReplyTo ?? attrs.in_reply_to;

  let replyMessage = inReplyToMessageId
    ? props.messages?.find(msg => sameMessageId(msg.id, inReplyToMessageId))
    : null;

  if (!replyMessage && inReplyToMessageId && currentChat.value?.messages) {
    replyMessage = currentChat.value.messages.find(msg =>
      sameMessageId(msg.id, inReplyToMessageId)
    );
  }

  if (
    !replyMessage &&
    inReplyToMessageId &&
    fetchedReplyMessages.has(inReplyToMessageId)
  ) {
    replyMessage = fetchedReplyMessages.get(inReplyToMessageId);
  }

  if (replyMessage) return useCamelCase(replyMessage);
  if (isAlbumQuote(parentMessage)) return ALBUM_QUOTE_PREVIEW;

  if (inReplyToMessageId && currentChat.value?.id) {
    fetchReplyMessage(inReplyToMessageId, currentChat.value.id);
    return null;
  }

  return null;
};
</script>

<template>
  <ul class="px-4 bg-n-surface-1">
    <slot name="beforeAll" />
    <template v-for="(message, index) in visibleMessages" :key="message.id">
      <slot
        v-if="firstVisibleUnreadId && message.id === firstVisibleUnreadId"
        name="unreadBadge"
      />
      <Message
        v-bind="message"
        :is-email-inbox="isAnEmailChannel"
        :in-reply-to="getInReplyToMessage(message)"
        :group-with-next="shouldGroupWithNext(index, visibleMessages)"
        :inbox-supports-reply-to="inboxSupportsReplyTo"
        :current-user-id="currentUserId"
        data-clarity-mask="True"
        @retry="emit('retry', message)"
      />
    </template>
    <slot name="after" />
  </ul>
</template>
